import {
	DeleteOutlined,
	EditOutlined,
	EllipsisOutlined,
	EyeOutlined,
} from "@ant-design/icons";
import { AnimationVariants, TapAnimation } from "@di-devs/base-components";
import { MIRAIC_NO_IMAGE_URL } from "@di-devs/constants";
import { Button, Descriptions, Divider, Dropdown, Image, Modal } from "antd";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import moment from "moment";
import { useEffect, useState } from "react";
import styled from "styled-components";
import type { Member, MemberAttribute } from "../../../../shared";
import {
	generateMemberAttributeAddressStr,
	memberAttributeUtils,
} from "../../../shared/utils/memberAttribute.utils";

const WHITE_COLOR = "#FFF";

const MemberActionsDiv = styled.div`
	.ant-dropdown-trigger {
		font-size: 18px;
		margin-right: 1.5em;
		position: relative;
		top: -4px;
	}
`;

export interface MemberDetailModalProps {
	visible: boolean;
	member: Member | null;
	memberAttributes?: MemberAttribute[];
	onClose: () => void;
	onEdit: () => void;
	onDelete: (member: Member) => void;
	getUploadPath?: (memberAttributeId: string) => string;
}

const MemberDetailModal: React.FC<MemberDetailModalProps> = (props) => {
	const {
		visible,
		member: currentMember,
		memberAttributes = [],
		onClose: hideMemberDetailModal,
		onEdit: showMemberEditDetailModal,
		onDelete: handleMemberDelete,
		getUploadPath = (memberAttributeId: string) =>
			memberAttributeUtils.getFileUrl(memberAttributeId, ""),
	} = props;

	const [dataMember, setDataMember] = useState<
		Array<MemberAttribute & { value: string | number | true | Date }>
	>([]);

	useEffect(() => {
		const dataMemberAttributes = memberAttributes?.map((item) => {
			const column = `memberAttributeId${item?.memberAttributeId}`;
			return {
				...item,
				value: currentMember?.[column as keyof Member] || "",
			};
		});

		setDataMember(dataMemberAttributes);
	}, [memberAttributes, currentMember]);

	const handleMemberAttributes = (
		item: MemberAttribute & { value: string | number | true | Date },
		index: number,
	) => {
		if (
			item?.type === "text" ||
			item?.type === "textarea" ||
			item?.type === "url" ||
			item?.type === "number_integer" ||
			item?.type === "number_float" ||
			item?.type === "radio" ||
			item?.type === "time" ||
			item?.type === "checkbox" ||
			item?.type === "email" ||
			item?.type === "telephone" ||
			item?.type === "firstName" ||
			item?.type === "lastName" ||
			item?.type === "firstNameKana" ||
			item?.type === "lastNameKana"
		) {
			return (
				<Descriptions.Item
					className="text-sm text-left"
					label={<p className="text-center">{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					<p className="text-sm">{item.value.toString() || "ー"}</p>
				</Descriptions.Item>
			);
		}
		if (item?.type === "address_postal") {
			let addressStr = "-";
			if (currentMember) {
				addressStr = generateMemberAttributeAddressStr({
					member: currentMember,
					memberAttributes,
					addrGroupSection: item.section,
				});
			}
			return (
				<Descriptions.Item
					className="text-sm text-left"
					label={<p className="text-center">{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					<p className="text-sm">{addressStr}</p>
				</Descriptions.Item>
			);
		}
		if (item?.type === "date") {
			return (
				<Descriptions.Item
					className="text-sm text-left"
					label={<p className="text-center">{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					<p className="text-sm">
						{item.value
							? dayjs(item.value.toString()).format("YYYY年M月D日")
							: "ー"}
					</p>
				</Descriptions.Item>
			);
		}
		if (item?.type === "select") {
			return (
				<Descriptions.Item
					className="text-sm text-left"
					label={<p className="text-center">{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					<p className="text-sm">{item.value.toString() || "ー"}</p>
				</Descriptions.Item>
			);
		}
		if (item?.type === "datetime") {
			return (
				<Descriptions.Item
					className="text-sm text-left"
					label={<p className="text-center">{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					<p className="text-sm">
						{item.value
							? dayjs(item.value.toString()).format("YYYY年M月D日 HH:mm")
							: "ー"}
					</p>
				</Descriptions.Item>
			);
		}
		if (item?.type === "color") {
			return (
				<Descriptions.Item
					className="text-sm text-left"
					label={<p className="text-center">{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					<div
						style={{
							backgroundColor: item.value.toString(),
							width: "50px",
							height: "50px",
							border: "1px solid #000",
						}}
					></div>
				</Descriptions.Item>
			);
		}
		if (item?.type === "image") {
			const imagePath = item.value
				? getUploadPath(item.memberAttributeId?.toString() || "") +
					`/${item.value}`
				: MIRAIC_NO_IMAGE_URL;

			return (
				<Descriptions.Item
					className="text-sm text-left"
					label={<p className="text-center">{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					<Image
						preview={true}
						className="w-20 h-20"
						src={imagePath}
						fallback={MIRAIC_NO_IMAGE_URL}
					/>
				</Descriptions.Item>
			);
		}
		if (item?.type === "video") {
			const videoPath = item.value
				? getUploadPath(item.memberAttributeId?.toString() || "") +
					`/${item.value}`
				: null;

			return (
				<Descriptions.Item
					className="text-sm text-left"
					label={<p className="text-center">{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					{videoPath ? (
						<video width="320" height="240" controls>
							<source src={videoPath} />
							Your browser does not support the video tag.
						</video>
					) : (
						<p>動画がありません</p>
					)}
				</Descriptions.Item>
			);
		}
		if (item?.type === "file") {
			const filePath = item.value
				? getUploadPath(item.memberAttributeId?.toString() || "") +
					`/${item.value}`
				: null;

			return (
				<Descriptions.Item
					className="text-sm text-left"
					label={<p className="text-center">{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					{filePath ? (
						<a href={filePath} download>
							{item.value.toString()}
						</a>
					) : (
						<p>ファイルがありません</p>
					)}
				</Descriptions.Item>
			);
		}
	};

	if (!visible || !currentMember) return null;

	const memberDetailActions = [
		{
			key: "0",
			label: (
				<>
					<EditOutlined className="text-base" />
					<span className="text-base ml-2">編集</span>
				</>
			),
			onClick: () => showMemberEditDetailModal(),
		},
		{
			key: "1",
			label: (
				<>
					<DeleteOutlined className="text-base" />
					<span className="text-base ml-2">削除</span>
				</>
			),
			onClick: () => handleMemberDelete(currentMember),
		},
	];

	return (
		<Modal
			title={
				<MemberActionsDiv className="flex justify-between">
					<span>お客様詳細</span>
					<Dropdown
						menu={{ items: memberDetailActions }}
						trigger={["click"]}
						placement="bottomRight"
					>
						<EllipsisOutlined />
					</Dropdown>
				</MemberActionsDiv>
			}
			open={visible}
			onCancel={hideMemberDetailModal}
			styles={{
				body: {
					maxHeight: "90vh",
					overflowY: "auto",
					overflowX: "hidden",
				},
			}}
			footer={null}
			destroyOnClose
			centered
		>
			<motion.div
				className="flex flex-col justify-center"
				variants={AnimationVariants.StaggerContainer}
				initial="hidden"
				animate="show"
				exit="hidden"
			>
				<motion.div variants={AnimationVariants.StaggerItem}>
					<div className="flex justify-center">
						{currentMember?.picUrl ? (
							<Image
								preview={{
									mask: <EyeOutlined />,
									src: currentMember.picUrl,
									maskClassName: "rounded-full",
								}}
								width={100}
								height={100}
								style={{
									border: `4px solid ${WHITE_COLOR}`,
								}}
								className="rounded-full"
								src={`${currentMember.picUrl}/large`}
								fallback={MIRAIC_NO_IMAGE_URL}
							/>
						) : (
							<Image
								preview={false}
								width={100}
								height={100}
								style={{
									border: `4px solid ${WHITE_COLOR}`,
								}}
								className="rounded-full"
								src={MIRAIC_NO_IMAGE_URL}
							/>
						)}
					</div>
				</motion.div>
				<motion.div variants={AnimationVariants.StaggerItem}>
					<Descriptions column={1} layout="horizontal" bordered>
						<Descriptions.Item
							className="text-left"
							label={<p className="text-center">ID</p>}
						>
							{currentMember?.memberId || "ー"}
						</Descriptions.Item>
						<Descriptions.Item
							className="text-left"
							label={<p className="text-center">会員コード</p>}
						>
							{currentMember?.memberCode || "ー"}
						</Descriptions.Item>
						<Descriptions.Item
							className="text-left"
							label={<p className="text-center">LINE名</p>}
						>
							{currentMember?.displayName || "ー"}
						</Descriptions.Item>
						{dataMember?.map((item, index) =>
							handleMemberAttributes(item, index),
						)}
						<Descriptions.Item
							className="text-left"
							label={<p className="text-center">会員登録日</p>}
						>
							{currentMember?.memberSince
								? moment(currentMember.memberSince).format("YYYY年M月D日")
								: "ー"}
						</Descriptions.Item>
					</Descriptions>
				</motion.div>
				<Divider />
				<motion.div
					className="flex justify-center"
					variants={AnimationVariants.StaggerItem}
				>
					<TapAnimation>
						<Button
							size="large"
							className="px-12"
							onClick={hideMemberDetailModal}
						>
							閉じる
						</Button>
					</TapAnimation>
				</motion.div>
			</motion.div>
		</Modal>
	);
};

export default MemberDetailModal;
